// The leg band: named date ranges shown in the calendar's all-day area,
// above the timed grid and the accommodation footer.
//
// Bars behave like all-day notes — drag to move, edge handles to stretch —
// and additionally accept a place dropped from the sidebar, which assigns it
// to that leg. Lanes are packed together with all-day notes (see
// WeekAllDayArea) so only genuinely overlapping ranges take a second row.
import { useMemo } from "react";
import clsx from "clsx";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { DayView, TripLeg } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import { useLegStore } from "../../store/legStore";
import { daysBetweenDates } from "../../utils/zonedTime";
import { dndIds } from "../../utils/dndIds";
import { useLegRangeGesture } from "./useLegRangeGesture";
import { legPalette } from "../../theme/colors";
import { Button } from "../Button";
import { useCalendarDndStore } from "../../store/calendarDndStore";
import type {
  BoardDragData,
  CalendarDateDropData,
  LegDropData,
} from "./dragTypes";
import { DateRangeResizeHandle } from "./DateRangeResizeHandle";

/**
 * One column's worth of drop surface behind the leg bars. The resize handles
 * locate their target date by hit-testing `data-calendar-date-target`, and a
 * dragged leg lands on the matching droppable — so the band has to provide its
 * own rather than borrowing the note rail's, which is not rendered at all when
 * a trip has no all-day notes.
 *
 * The id is namespaced rather than reusing `dndIds.calendarDate`: dnd-kit keys
 * droppables by id in a plain Map, so when the note rail is also on screen the
 * two registrations would clobber each other and unmounting the rail would
 * take the band's surviving entry with it.
 */
function LegDateDropTarget({ date, zone }: { date: string; zone: string }) {
  const preview = useCalendarDndStore((state) => state.dateRangePreview);
  const inPreview = Boolean(
    preview && date >= preview.startDate && date <= preview.endDate,
  );
  const isTarget = preview?.targetDate === date;
  const { setNodeRef, isOver } = useDroppable({
    id: dndIds.legBandDate(date),
    data: { type: "calendar-date", date, zone } satisfies CalendarDateDropData,
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "min-w-0 rounded-md transition-colors",
        isOver && "bg-calendar-all-day-bg",
        inPreview &&
          (preview?.invalid ? "bg-error-bg-strong" : "bg-calendar-all-day-bg"),
        isTarget && "ring-2 ring-inset ring-info",
      )}
      data-calendar-date-target={date}
      data-calendar-zone={zone}
      data-leg-band-target
    />
  );
}

function LegRange({
  leg,
  row,
  startColumn,
  endColumn,
  dateCount,
  clippedStart,
  clippedEnd,
  zoneForDate,
  isReadOnly,
}: {
  leg: TripLeg;
  row: number;
  startColumn: number;
  endColumn: number;
  dateCount: number;
  clippedStart: boolean;
  clippedEnd: boolean;
  zoneForDate: (date: string) => string;
  isReadOnly: boolean;
}) {
  const openLegEditor = useUIStore((state) => state.openLegEditor);
  const setScope = useUIStore((state) => state.setSidebarLegFocus);
  const palette = legPalette(leg.color);
  const canChange = !isReadOnly;

  const dragData = useMemo<Extract<BoardDragData, { type: "leg-range" }>>(
    () => ({
      type: "leg-range",
      legId: leg.id,
      startDate: leg.startDate,
      endDate: leg.endDate,
      grabbedIndex: 0,
      dateCount,
      label: leg.name,
    }),
    [dateCount, leg],
  );

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.legRange(leg.id),
    data: dragData,
    disabled: !canChange,
  });

  // A place dropped here joins the leg.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dndIds.legDropTarget(leg.id),
    data: { type: "leg", legId: leg.id } satisfies LegDropData,
    disabled: isReadOnly,
  });

  const { wasDragging, previewForEdge, commitEdge } = useLegRangeGesture(
    leg,
    isDragging,
  );

  return (
    <div
      ref={setDropRef}
      data-leg-drop-id={dndIds.legDropTarget(leg.id)}
      // `group/range` is the name DateRangeResizeHandle reveals itself under.
      className="group/range pointer-events-auto relative z-10 min-w-0"
      style={{ gridColumn: `${startColumn} / ${endColumn}`, gridRow: row }}
      onPointerDownCapture={(event) => {
        if (!canChange) return;
        const target = document
          .elementsFromPoint(event.clientX, event.clientY)
          .find((element) =>
            (element as HTMLElement).hasAttribute?.("data-leg-band-target"),
          ) as HTMLElement | undefined;
        const targetDate = target?.dataset.calendarDateTarget;
        if (targetDate) {
          dragData.grabbedIndex = daysBetweenDates(leg.startDate, targetDate);
        }
      }}
    >
      {/* The bar and its focus action are siblings inside the pill: the focus
          control cannot live inside the bar's button, and a span with
          role="button" would only hide the nesting from the validator. */}
      <div
        className={clsx(
          "flex h-6 w-full min-w-0 items-center border pl-2 pr-0.5 shadow-sm",
          clippedStart ? "rounded-l-none" : "rounded-l-full",
          clippedEnd ? "rounded-r-none" : "rounded-r-full",
          isDragging && "opacity-30",
          isOver && "ring-2 ring-info",
        )}
        style={{
          backgroundColor: palette.bg,
          borderColor: palette.border,
          color: palette.text,
        }}
      >
        <Button
          ref={setNodeRef}
          variant="unstyled"
          {...(canChange ? attributes : {})}
          {...(canChange ? listeners : {})}
          onClick={(event) => {
            event.stopPropagation();
            if (wasDragging.current) {
              wasDragging.current = false;
              return;
            }
            openLegEditor(leg.id);
          }}
          className={clsx(
            "flex h-full min-w-0 flex-1 items-center gap-1.5 text-left text-2xs font-extrabold focus-visible:outline-2 focus-visible:outline-offset-2",
            canChange
              ? "cursor-grab touch-none hover:brightness-95 active:cursor-grabbing"
              : "cursor-pointer",
          )}
          title={
            canChange
              ? `${leg.name} · ${leg.startDate} – ${leg.endDate}. Drag to move, drag an edge to change dates, or drop a place to add it.`
              : `${leg.name} · ${leg.startDate} – ${leg.endDate}`
          }
          aria-label={`Open leg ${leg.name}`}
          data-testid={`leg-range-${leg.id}`}
        >
          {clippedStart && (
            <span aria-hidden className="shrink-0">
              ‹
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{leg.name}</span>
          {clippedEnd && (
            <span aria-hidden className="shrink-0">
              ›
            </span>
          )}
        </Button>
        <Button
          variant="icon"
          size="sm"
          icon="filter_center_focus"
          aria-label={`Focus the sidebar on ${leg.name}`}
          title={`Focus the sidebar on ${leg.name}`}
          className="size-5 shrink-0 text-current hover:bg-white/60 hover:text-current"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setScope(leg.id);
          }}
        />
      </div>
      {canChange && !clippedStart && (
        <DateRangeResizeHandle
          edge="start"
          label={leg.name}
          currentDate={leg.startDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("start", target)}
          commitTarget={(target) => commitEdge("start", target)}
        />
      )}
      {canChange && !clippedEnd && (
        <DateRangeResizeHandle
          edge="end"
          label={leg.name}
          currentDate={leg.endDate}
          zoneForDate={zoneForDate}
          previewForTarget={(target) => previewForEdge("end", target)}
          commitTarget={(target) => commitEdge("end", target)}
        />
      )}
    </div>
  );
}

/** Creates a leg spanning `dayViews` and opens its editor. */
export function AddLegButton({ dayViews }: { dayViews: DayView[] }) {
  const legCount = useLegStore((state) => state.legs.length);
  const trip = useTripStore((state) => state.trip);
  const createLeg = useLegStore((state) => state.createLeg);
  const openLegEditor = useUIStore((state) => state.openLegEditor);
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;

  const addLeg = async () => {
    if (!trip || !firstDate) return;
    const leg = await createLeg(trip.id, {
      name: "New leg",
      startDate: firstDate,
      endDate: lastDate ?? firstDate,
      color: legCount % 8,
    });
    if (leg) openLegEditor(leg.id);
  };

  return (
    <Button
      variant="icon"
      size="sm"
      icon="add"
      aria-label="Add leg"
      title="Add a leg covering these dates"
      onClick={addLeg}
    />
  );
}

/**
 * Leg ranges aligned to the calendar's date columns. Renders into the parent
 * all-day grid (which must be `relative` and share the day columns) so legs
 * and all-day notes pack into the same lanes.
 */
export function CalendarLegBand({
  dayViews,
  legs,
  lanes,
  isReadOnly,
}: {
  dayViews: DayView[];
  legs: TripLeg[];
  /** Zero-based lane per leg id. */
  lanes: Map<string, number>;
  isReadOnly: boolean;
}) {
  const firstDate = dayViews[0]?.date;
  const lastDate = dayViews[dayViews.length - 1]?.date;

  const dateIndex = useMemo(
    () => new Map(dayViews.map((day, index) => [day.date, index])),
    [dayViews],
  );
  const zoneByDate = useMemo(
    () => new Map(dayViews.map((day) => [day.date, day.zone])),
    [dayViews],
  );
  const zoneForDate = (date: string) =>
    zoneByDate.get(date) ?? dayViews[0]?.zone ?? "UTC";

  if (!firstDate || !lastDate) return null;

  return (
    <>
      <div
        className="pointer-events-auto absolute inset-0 grid gap-x-2.5"
        style={{
          gridTemplateColumns: `repeat(${dayViews.length}, minmax(0, 1fr))`,
        }}
        data-testid="calendar-leg-band"
      >
        {dayViews.map((day) => (
          <LegDateDropTarget key={day.date} date={day.date} zone={day.zone} />
        ))}
      </div>
      {legs.map((leg) => {
        // Clip to the visible window; a leg may start before or end after it.
        const startIndex = dateIndex.get(
          leg.startDate < firstDate ? firstDate : leg.startDate,
        );
        const endIndex = dateIndex.get(
          leg.endDate > lastDate ? lastDate : leg.endDate,
        );
        if (startIndex == null || endIndex == null) return null;
        return (
          <LegRange
            key={leg.id}
            leg={leg}
            row={(lanes.get(leg.id) ?? 0) + 1}
            startColumn={startIndex + 1}
            endColumn={endIndex + 2}
            dateCount={endIndex - startIndex + 1}
            clippedStart={leg.startDate < firstDate}
            clippedEnd={leg.endDate > lastDate}
            zoneForDate={zoneForDate}
            isReadOnly={isReadOnly}
          />
        );
      })}
    </>
  );
}
